import { LINEAR_CURVE } from '../../core/params/curve';
import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';
import { SHAPE_OPTIONS } from '../../core/shapes';

export const CIRCLE_PACKING_PARAMS = [
  // ── Packung ───────────────────────────────────────────────────────────────
  {
    kind: 'int',
    key: 'candidates',
    label: 'Kandidaten',
    min: 100,
    max: 20000,
    step: 1,
    scale: 'log',
    default: 6000,
    bulkRange: [1200, 14000],
    group: 'Packung',
    tooltip: 'Wie viele Platzierungsversuche unternommen werden. Mehr = dichter und feiner.',
  },
  {
    kind: 'range',
    key: 'radius',
    label: 'Radius',
    min: 1,
    max: 400,
    step: 0.5,
    default: [3, 74],
    unit: 'px',
    scale: 'log',
    precision: 1,
    group: 'Packung',
    tooltip: 'Kleinster und grösster erlaubter Radius.',
  },
  {
    kind: 'float',
    key: 'gap',
    label: 'Abstand',
    min: 0,
    max: 40,
    step: 0.25,
    default: 2.5,
    unit: 'px',
    bulkRange: [0, 14],
    group: 'Packung',
    tooltip: 'Mindestabstand zwischen zwei Formen.',
  },
  {
    kind: 'float',
    key: 'textGap',
    label: 'Abstand zum Text',
    min: 0,
    max: 120,
    step: 0.5,
    default: 6,
    unit: 'px',
    bulkRange: [0, 40],
    group: 'Packung',
    tooltip: 'Wie weit die Formen von den Buchstaben wegbleiben.',
  },
  {
    kind: 'enum',
    key: 'mode',
    label: 'Bereich',
    default: 'outside',
    segmented: true,
    group: 'Packung',
    options: [
      { value: 'outside', label: 'Aussen' },
      { value: 'inside', label: 'Innen' },
      { value: 'both', label: 'Beides' },
    ],
    tooltip: 'Wo gepackt wird. „Beides“ färbt innen und aussen unterschiedlich.',
  },
  {
    kind: 'enum',
    key: 'placement',
    label: 'Verteilung',
    default: 'poisson',
    group: 'Packung',
    options: [
      { value: 'random', label: 'Zufällig' },
      { value: 'poisson', label: 'Poisson' },
      { value: 'nearText', label: 'Dicht am Text' },
      { value: 'farText', label: 'Dicht am Rand' },
    ],
    tooltip: 'Wie die Kandidatenpunkte über die Fläche verteilt werden.',
  },
  {
    kind: 'int',
    key: 'perStep',
    label: 'Kandidaten pro Frame',
    min: 50,
    max: 5000,
    step: 10,
    default: 900,
    neverRandomise: true,
    group: 'Packung',
    tooltip: 'Nur Aufbaugeschwindigkeit — beeinflusst das Endbild nicht.',
  },

  // ── Grösse ────────────────────────────────────────────────────────────────
  {
    kind: 'curve',
    key: 'radiusByDistance',
    label: 'Radius nach Textdistanz',
    default: LINEAR_CURVE,
    xLabel: 'nah am Text',
    yLabel: 'fern',
    group: 'Grösse',
    tooltip: 'Skaliert den erlaubten Radius abhängig vom Abstand zum Text.',
  },
  {
    kind: 'float',
    key: 'distanceReach',
    label: 'Reichweite der Kopplung',
    min: 20,
    max: 1200,
    step: 5,
    scale: 'log',
    default: 340,
    unit: 'px',
    bulkRange: [80, 700],
    group: 'Grösse',
    tooltip: 'Distanz, über die die Radiuskurve von links nach rechts durchläuft.',
  },

  // ── Form ──────────────────────────────────────────────────────────────────
  {
    kind: 'enum',
    key: 'shape',
    label: 'Form',
    default: 'circle',
    group: 'Form',
    options: SHAPE_OPTIONS,
  },
  {
    kind: 'int',
    key: 'starPoints',
    label: 'Zacken',
    min: 3,
    max: 16,
    step: 1,
    default: 5,
    group: 'Form',
    showIf: (v) => v.shape === 'star' || v.shape === 'mixed',
  },
  {
    kind: 'float',
    key: 'rotationJitter',
    label: 'Rotationsstreuung',
    min: 0,
    max: 180,
    step: 1,
    default: 0,
    unit: '°',
    group: 'Form',
    showIf: (v) => v.shape !== 'circle',
  },

  // ── Darstellung ───────────────────────────────────────────────────────────
  {
    kind: 'enum',
    key: 'style',
    label: 'Stil',
    default: 'mixed',
    group: 'Darstellung',
    options: [
      { value: 'fill', label: 'Füllung' },
      { value: 'stroke', label: 'Kontur' },
      { value: 'rings', label: 'Konzentrische Ringe' },
      { value: 'mixed', label: 'Gemischt' },
    ],
  },
  {
    kind: 'float',
    key: 'fillRatio',
    label: 'Anteil gefüllt',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.35,
    group: 'Darstellung',
    tooltip: 'Wahrscheinlichkeit, dass eine Form gefüllt statt konturiert gezeichnet wird.',
    showIf: (v) => v.style === 'mixed',
  },
  {
    kind: 'int',
    key: 'rings',
    label: 'Ringe',
    min: 2,
    max: 14,
    step: 1,
    default: 4,
    group: 'Darstellung',
    showIf: (v) => v.style === 'rings' || v.style === 'mixed',
  },
  {
    kind: 'float',
    key: 'lineWidth',
    label: 'Strichstärke',
    min: 0.25,
    max: 14,
    step: 0.25,
    default: 1.5,
    unit: 'px',
    bulkRange: [0.5, 5],
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 0.92,
    group: 'Darstellung',
  },

  // ── Farbe ─────────────────────────────────────────────────────────────────
  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'distance',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'size', label: 'Nach Grösse' },
      { value: 'order', label: 'Nach Reihenfolge' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'bauhaus' },
    group: 'Farbe',
  },
  {
    kind: 'color',
    key: 'colorInside',
    label: 'Farbe innen',
    default: '#2b6cff',
    group: 'Farbe',
    tooltip: 'Formen innerhalb der Buchstaben, wenn der Bereich „Beides“ gewählt ist.',
    showIf: (v) => v.mode === 'both',
  },
] as const satisfies ParamSchema;

export type CirclePackingValues = SchemaValues<typeof CIRCLE_PACKING_PARAMS>;
